import { TypeSettingsState } from 'base/styleguide/model';
import { makeObservable, observable } from 'mobx';

type TypeSettingSize = TypeSettingsState['size'];
type TypeSettingWeight = TypeSettingsState['weight'];
type TypeSettingStyle = TypeSettingsState['style'];

export const TYPE_SETTING_SIZE_LABELS: Record<TypeSettingSize, string> = {
  small: 'Small',
  regular: 'Regular',
  large: 'Large',
};

export const TYPE_SETTING_WEIGHT_LABELS: Record<TypeSettingWeight, string> = {
  regular: 'Normal',
  medium: 'Medium',
  bold: 'Bold',
};

export const TYPE_SETTING_STYLE_LABELS: Record<TypeSettingStyle, string> = {
  default: 'Default',
  spacious: 'Spacious',
  uppercase: 'Uppercase',
};

type TypeSettingSizeKey =
  | 'heading-h1-font-size'
  | 'heading-h2-font-size'
  | 'heading-h3-font-size'
  | 'heading-h4-font-size'
  | 'heading-h5-font-size'
  | 'heading-h6-font-size'
  | 'text-large-font-size'
  | 'text-medium-font-size'
  | 'text-regular-font-size'
  | 'text-small-font-size'
  | 'text-tiny-font-size'
  | 'heading-h1-font-size-mobile'
  | 'heading-h2-font-size-mobile'
  | 'heading-h3-font-size-mobile'
  | 'heading-h4-font-size-mobile'
  | 'heading-h5-font-size-mobile'
  | 'heading-h6-font-size-mobile'
  | 'text-large-font-size-mobile'
  | 'text-medium-font-size-mobile'
  | 'text-regular-font-size-mobile'
  | 'text-small-font-size-mobile'
  | 'text-tiny-font-size-mobile';

export const sizePixels: Record<
  TypeSettingSize,
  Record<TypeSettingSizeKey, number>
> = {
  small: {
    ['heading-h1-font-size']: 56,
    ['heading-h2-font-size']: 48,
    ['heading-h3-font-size']: 40,
    ['heading-h4-font-size']: 32,
    ['heading-h5-font-size']: 24,
    ['heading-h6-font-size']: 20,

    ['text-large-font-size']: 20,
    ['text-medium-font-size']: 18,
    ['text-regular-font-size']: 16,
    ['text-small-font-size']: 14,
    ['text-tiny-font-size']: 12,

    ['heading-h1-font-size-mobile']: 40,
    ['heading-h2-font-size-mobile']: 36,
    ['heading-h3-font-size-mobile']: 32,
    ['heading-h4-font-size-mobile']: 24,
    ['heading-h5-font-size-mobile']: 20,
    ['heading-h6-font-size-mobile']: 18,

    ['text-large-font-size-mobile']: 18,
    ['text-medium-font-size-mobile']: 16,
    ['text-regular-font-size-mobile']: 14,
    ['text-small-font-size-mobile']: 12,
    ['text-tiny-font-size-mobile']: 10,
  },
  regular: {
    ['heading-h1-font-size']: 72,
    ['heading-h2-font-size']: 52,
    ['heading-h3-font-size']: 44,
    ['heading-h4-font-size']: 36,
    ['heading-h5-font-size']: 28,
    ['heading-h6-font-size']: 22,

    ['text-large-font-size']: 22,
    ['text-medium-font-size']: 18,
    ['text-regular-font-size']: 16,
    ['text-small-font-size']: 14,
    ['text-tiny-font-size']: 12,

    ['heading-h1-font-size-mobile']: 44,
    ['heading-h2-font-size-mobile']: 40,
    ['heading-h3-font-size-mobile']: 32,
    ['heading-h4-font-size-mobile']: 24,
    ['heading-h5-font-size-mobile']: 20,
    ['heading-h6-font-size-mobile']: 18,

    ['text-large-font-size-mobile']: 18,
    ['text-medium-font-size-mobile']: 16,
    ['text-regular-font-size-mobile']: 12,
    ['text-small-font-size-mobile']: 12,
    ['text-tiny-font-size-mobile']: 10,
  },
  large: {
    ['heading-h1-font-size']: 84,
    ['heading-h2-font-size']: 60,
    ['heading-h3-font-size']: 48,
    ['heading-h4-font-size']: 40,
    ['heading-h5-font-size']: 32,
    ['heading-h6-font-size']: 26,

    ['text-large-font-size']: 26,
    ['text-medium-font-size']: 20,
    ['text-regular-font-size']: 18,
    ['text-small-font-size']: 16,
    ['text-tiny-font-size']: 12,

    ['heading-h1-font-size-mobile']: 48,
    ['heading-h2-font-size-mobile']: 44,
    ['heading-h3-font-size-mobile']: 32,
    ['heading-h4-font-size-mobile']: 24,
    ['heading-h5-font-size-mobile']: 20,
    ['heading-h6-font-size-mobile']: 18,

    ['text-large-font-size-mobile']: 18,
    ['text-medium-font-size-mobile']: 16,
    ['text-regular-font-size-mobile']: 14,
    ['text-small-font-size-mobile']: 12,
    ['text-tiny-font-size-mobile']: 10,
  },
};

export const sizeCSSVariables: Record<
  TypeSettingSize,
  Record<TypeSettingSizeKey, string>
> = {
  small: {
    ['heading-h1-font-size']: `${sizePixels.small['heading-h1-font-size']}px`,
    ['heading-h2-font-size']: `${sizePixels.small['heading-h2-font-size']}px`,
    ['heading-h3-font-size']: `${sizePixels.small['heading-h3-font-size']}px`,
    ['heading-h4-font-size']: `${sizePixels.small['heading-h4-font-size']}px`,
    ['heading-h5-font-size']: `${sizePixels.small['heading-h5-font-size']}px`,
    ['heading-h6-font-size']: `${sizePixels.small['heading-h6-font-size']}px`,

    ['text-large-font-size']: `${sizePixels.small['text-large-font-size']}px`,
    ['text-medium-font-size']: `${sizePixels.small['text-medium-font-size']}px`,
    ['text-regular-font-size']: `${sizePixels.small['text-regular-font-size']}px`,
    ['text-small-font-size']: `${sizePixels.small['text-small-font-size']}px`,
    ['text-tiny-font-size']: `${sizePixels.small['text-tiny-font-size']}px`,

    ['heading-h1-font-size-mobile']: `${sizePixels.small['heading-h1-font-size-mobile']}px`,
    ['heading-h2-font-size-mobile']: `${sizePixels.small['heading-h2-font-size-mobile']}px`,
    ['heading-h3-font-size-mobile']: `${sizePixels.small['heading-h3-font-size-mobile']}px`,
    ['heading-h4-font-size-mobile']: `${sizePixels.small['heading-h4-font-size-mobile']}px`,
    ['heading-h5-font-size-mobile']: `${sizePixels.small['heading-h5-font-size-mobile']}px`,
    ['heading-h6-font-size-mobile']: `${sizePixels.small['heading-h6-font-size-mobile']}px`,

    ['text-large-font-size-mobile']: `${sizePixels.small['text-large-font-size-mobile']}px`,
    ['text-medium-font-size-mobile']: `${sizePixels.small['text-medium-font-size-mobile']}px`,
    ['text-regular-font-size-mobile']: `${sizePixels.small['text-regular-font-size-mobile']}px`,
    ['text-small-font-size-mobile']: `${sizePixels.small['text-small-font-size-mobile']}px`,
    ['text-tiny-font-size-mobile']: `${sizePixels.small['text-tiny-font-size-mobile']}px`,
  },
  regular: {
    ['heading-h1-font-size']: `${sizePixels.regular['heading-h1-font-size']}px`,
    ['heading-h2-font-size']: `${sizePixels.regular['heading-h2-font-size']}px`,
    ['heading-h3-font-size']: `${sizePixels.regular['heading-h3-font-size']}px`,
    ['heading-h4-font-size']: `${sizePixels.regular['heading-h4-font-size']}px`,
    ['heading-h5-font-size']: `${sizePixels.regular['heading-h5-font-size']}px`,
    ['heading-h6-font-size']: `${sizePixels.regular['heading-h6-font-size']}px`,

    ['text-large-font-size']: `${sizePixels.regular['text-large-font-size']}px`,
    ['text-medium-font-size']: `${sizePixels.regular['text-medium-font-size']}px`,
    ['text-regular-font-size']: `${sizePixels.regular['text-regular-font-size']}px`,
    ['text-small-font-size']: `${sizePixels.regular['text-small-font-size']}px`,
    ['text-tiny-font-size']: `${sizePixels.regular['text-tiny-font-size']}px`,

    ['heading-h1-font-size-mobile']: `${sizePixels.regular['heading-h1-font-size-mobile']}px`,
    ['heading-h2-font-size-mobile']: `${sizePixels.regular['heading-h2-font-size-mobile']}px`,
    ['heading-h3-font-size-mobile']: `${sizePixels.regular['heading-h3-font-size-mobile']}px`,
    ['heading-h4-font-size-mobile']: `${sizePixels.regular['heading-h4-font-size-mobile']}px`,
    ['heading-h5-font-size-mobile']: `${sizePixels.regular['heading-h5-font-size-mobile']}px`,
    ['heading-h6-font-size-mobile']: `${sizePixels.regular['heading-h6-font-size-mobile']}px`,

    ['text-large-font-size-mobile']: `${sizePixels.regular['text-large-font-size-mobile']}px`,
    ['text-medium-font-size-mobile']: `${sizePixels.regular['text-medium-font-size-mobile']}px`,
    ['text-regular-font-size-mobile']: `${sizePixels.regular['text-regular-font-size-mobile']}px`,
    ['text-small-font-size-mobile']: `${sizePixels.regular['text-small-font-size-mobile']}px`,
    ['text-tiny-font-size-mobile']: `${sizePixels.regular['text-tiny-font-size-mobile']}px`,
  },
  large: {
    ['heading-h1-font-size']: `${sizePixels.large['heading-h1-font-size']}px`,
    ['heading-h2-font-size']: `${sizePixels.large['heading-h2-font-size']}px`,
    ['heading-h3-font-size']: `${sizePixels.large['heading-h3-font-size']}px`,
    ['heading-h4-font-size']: `${sizePixels.large['heading-h4-font-size']}px`,
    ['heading-h5-font-size']: `${sizePixels.large['heading-h5-font-size']}px`,
    ['heading-h6-font-size']: `${sizePixels.large['heading-h6-font-size']}px`,

    ['text-large-font-size']: `${sizePixels.large['text-large-font-size']}px`,
    ['text-medium-font-size']: `${sizePixels.large['text-medium-font-size']}px`,
    ['text-regular-font-size']: `${sizePixels.large['text-regular-font-size']}px`,
    ['text-small-font-size']: `${sizePixels.large['text-small-font-size']}px`,
    ['text-tiny-font-size']: `${sizePixels.large['text-tiny-font-size']}px`,

    ['heading-h1-font-size-mobile']: `${sizePixels.large['heading-h1-font-size-mobile']}px`,
    ['heading-h2-font-size-mobile']: `${sizePixels.large['heading-h2-font-size-mobile']}px`,
    ['heading-h3-font-size-mobile']: `${sizePixels.large['heading-h3-font-size-mobile']}px`,
    ['heading-h4-font-size-mobile']: `${sizePixels.large['heading-h4-font-size-mobile']}px`,
    ['heading-h5-font-size-mobile']: `${sizePixels.large['heading-h5-font-size-mobile']}px`,
    ['heading-h6-font-size-mobile']: `${sizePixels.large['heading-h6-font-size-mobile']}px`,

    ['text-large-font-size-mobile']: `${sizePixels.large['text-large-font-size-mobile']}px`,
    ['text-medium-font-size-mobile']: `${sizePixels.large['text-medium-font-size-mobile']}px`,
    ['text-regular-font-size-mobile']: `${sizePixels.large['text-regular-font-size-mobile']}px`,
    ['text-small-font-size-mobile']: `${sizePixels.large['text-small-font-size-mobile']}px`,
    ['text-tiny-font-size-mobile']: `${sizePixels.large['text-tiny-font-size-mobile']}px`,
  },
};

export const sizeCSSRemVariables: Record<
  TypeSettingSize,
  Record<TypeSettingSizeKey, `${number}rem`>
> = {
  small: {
    'heading-h1-font-size': `${
      sizePixels.small['heading-h1-font-size'] / 16
    }rem`,
    'heading-h2-font-size': `${
      sizePixels.small['heading-h2-font-size'] / 16
    }rem`,
    'heading-h3-font-size': `${
      sizePixels.small['heading-h3-font-size'] / 16
    }rem`,
    'heading-h4-font-size': `${
      sizePixels.small['heading-h4-font-size'] / 16
    }rem`,
    'heading-h5-font-size': `${
      sizePixels.small['heading-h5-font-size'] / 16
    }rem`,
    'heading-h6-font-size': `${
      sizePixels.small['heading-h6-font-size'] / 16
    }rem`,

    'text-large-font-size': `${
      sizePixels.small['text-large-font-size'] / 16
    }rem`,
    'text-medium-font-size': `${
      sizePixels.small['text-medium-font-size'] / 16
    }rem`,
    'text-regular-font-size': `${
      sizePixels.small['text-regular-font-size'] / 16
    }rem`,
    'text-small-font-size': `${
      sizePixels.small['text-small-font-size'] / 16
    }rem`,
    'text-tiny-font-size': `${sizePixels.small['text-tiny-font-size'] / 16}rem`,
    'heading-h1-font-size-mobile': `${
      sizePixels.small['heading-h1-font-size-mobile'] / 16
    }rem`,
    'heading-h2-font-size-mobile': `${
      sizePixels.small['heading-h2-font-size-mobile'] / 16
    }rem`,
    'heading-h3-font-size-mobile': `${
      sizePixels.small['heading-h3-font-size-mobile'] / 16
    }rem`,
    'heading-h4-font-size-mobile': `${
      sizePixels.small['heading-h4-font-size-mobile'] / 16
    }rem`,
    'heading-h5-font-size-mobile': `${
      sizePixels.small['heading-h5-font-size-mobile'] / 16
    }rem`,
    'heading-h6-font-size-mobile': `${
      sizePixels.small['heading-h6-font-size-mobile'] / 16
    }rem`,
    'text-large-font-size-mobile': `${
      sizePixels.small['text-large-font-size-mobile'] / 16
    }rem`,
    'text-medium-font-size-mobile': `${
      sizePixels.small['text-medium-font-size-mobile'] / 16
    }rem`,
    'text-regular-font-size-mobile': `${
      sizePixels.small['text-regular-font-size-mobile'] / 16
    }rem`,
    'text-small-font-size-mobile': `${
      sizePixels.small['text-small-font-size-mobile'] / 16
    }rem`,
    'text-tiny-font-size-mobile': `${
      sizePixels.small['text-tiny-font-size-mobile'] / 16
    }rem`,
  },
  regular: {
    'heading-h1-font-size': `${
      sizePixels.regular['heading-h1-font-size'] / 16
    }rem`,
    'heading-h2-font-size': `${
      sizePixels.regular['heading-h2-font-size'] / 16
    }rem`,
    'heading-h3-font-size': `${
      sizePixels.regular['heading-h3-font-size'] / 16
    }rem`,
    'heading-h4-font-size': `${
      sizePixels.regular['heading-h4-font-size'] / 16
    }rem`,
    'heading-h5-font-size': `${
      sizePixels.regular['heading-h5-font-size'] / 16
    }rem`,
    'heading-h6-font-size': `${
      sizePixels.regular['heading-h6-font-size'] / 16
    }rem`,
    'text-large-font-size': `${
      sizePixels.regular['text-large-font-size'] / 16
    }rem`,
    'text-medium-font-size': `${
      sizePixels.regular['text-medium-font-size'] / 16
    }rem`,
    'text-regular-font-size': `${
      sizePixels.regular['text-regular-font-size'] / 16
    }rem`,
    'text-small-font-size': `${
      sizePixels.regular['text-small-font-size'] / 16
    }rem`,
    'text-tiny-font-size': `${
      sizePixels.regular['text-tiny-font-size'] / 16
    }rem`,
    'heading-h1-font-size-mobile': `${
      sizePixels.regular['heading-h1-font-size-mobile'] / 16
    }rem`,
    'heading-h2-font-size-mobile': `${
      sizePixels.regular['heading-h2-font-size-mobile'] / 16
    }rem`,
    'heading-h3-font-size-mobile': `${
      sizePixels.regular['heading-h3-font-size-mobile'] / 16
    }rem`,
    'heading-h4-font-size-mobile': `${
      sizePixels.regular['heading-h4-font-size-mobile'] / 16
    }rem`,
    'heading-h5-font-size-mobile': `${
      sizePixels.regular['heading-h5-font-size-mobile'] / 16
    }rem`,
    'heading-h6-font-size-mobile': `${
      sizePixels.regular['heading-h6-font-size-mobile'] / 16
    }rem`,
    'text-large-font-size-mobile': `${
      sizePixels.regular['text-large-font-size-mobile'] / 16
    }rem`,
    'text-medium-font-size-mobile': `${
      sizePixels.regular['text-medium-font-size-mobile'] / 16
    }rem`,
    'text-regular-font-size-mobile': `${
      sizePixels.regular['text-regular-font-size-mobile'] / 16
    }rem`,
    'text-small-font-size-mobile': `${
      sizePixels.regular['text-small-font-size-mobile'] / 16
    }rem`,
    'text-tiny-font-size-mobile': `${
      sizePixels.regular['text-tiny-font-size-mobile'] / 16
    }rem`,
  },
  large: {
    'heading-h1-font-size': `${
      sizePixels.large['heading-h1-font-size'] / 16
    }rem`,
    'heading-h2-font-size': `${
      sizePixels.large['heading-h2-font-size'] / 16
    }rem`,
    'heading-h3-font-size': `${
      sizePixels.large['heading-h3-font-size'] / 16
    }rem`,
    'heading-h4-font-size': `${
      sizePixels.large['heading-h4-font-size'] / 16
    }rem`,
    'heading-h5-font-size': `${
      sizePixels.large['heading-h5-font-size'] / 16
    }rem`,
    'heading-h6-font-size': `${
      sizePixels.large['heading-h6-font-size'] / 16
    }rem`,

    'text-large-font-size': `${
      sizePixels.large['text-large-font-size'] / 16
    }rem`,
    'text-medium-font-size': `${
      sizePixels.large['text-medium-font-size'] / 16
    }rem`,
    'text-regular-font-size': `${
      sizePixels.large['text-regular-font-size'] / 16
    }rem`,
    'text-small-font-size': `${
      sizePixels.large['text-small-font-size'] / 16
    }rem`,
    'text-tiny-font-size': `${sizePixels.large['text-tiny-font-size'] / 16}rem`,
    'heading-h1-font-size-mobile': `${
      sizePixels.large['heading-h1-font-size-mobile'] / 16
    }rem`,
    'heading-h2-font-size-mobile': `${
      sizePixels.large['heading-h2-font-size-mobile'] / 16
    }rem`,
    'heading-h3-font-size-mobile': `${
      sizePixels.large['heading-h3-font-size-mobile'] / 16
    }rem`,
    'heading-h4-font-size-mobile': `${
      sizePixels.large['heading-h4-font-size-mobile'] / 16
    }rem`,
    'heading-h5-font-size-mobile': `${
      sizePixels.large['heading-h5-font-size-mobile'] / 16
    }rem`,
    'heading-h6-font-size-mobile': `${
      sizePixels.large['heading-h6-font-size-mobile'] / 16
    }rem`,

    'text-large-font-size-mobile': `${
      sizePixels.large['text-large-font-size-mobile'] / 16
    }rem`,
    'text-medium-font-size-mobile': `${
      sizePixels.large['text-medium-font-size-mobile'] / 16
    }rem`,
    'text-regular-font-size-mobile': `${
      sizePixels.regular['text-regular-font-size-mobile'] / 16
    }rem`,
    'text-small-font-size-mobile': `${
      sizePixels.regular['text-small-font-size-mobile'] / 16
    }rem`,
    'text-tiny-font-size-mobile': `${
      sizePixels.regular['text-tiny-font-size-mobile'] / 16
    }rem`,
  },
};

type TypeSettingWeightKey = 'heading-font-weight';

export const weightCSSVariables: Record<
  TypeSettingWeight,
  Record<TypeSettingWeightKey, string>
> = {
  regular: {
    ['heading-font-weight']: '400',
  },
  medium: {
    ['heading-font-weight']: '500',
  },
  bold: {
    ['heading-font-weight']: '700',
  },
};

type LetterSpacingKey =
  | 'heading-h1'
  | 'heading-h2'
  | 'heading-h3'
  | 'heading-h4'
  | 'heading-h5'
  | 'heading-h6';

export const letterSpacingValues: Record<
  TypeSettingStyle,
  Record<TypeSettingSize, Record<LetterSpacingKey, number>>
> = {
  default: {
    small: {
      ['heading-h1']: -0.01,
      ['heading-h2']: -0.01,
      ['heading-h3']: -0.01,
      ['heading-h4']: -0.01,
      ['heading-h5']: -0.01,
      ['heading-h6']: -0.01,
    },
    regular: {
      ['heading-h1']: -0.01,
      ['heading-h2']: -0.01,
      ['heading-h3']: -0.01,
      ['heading-h4']: -0.01,
      ['heading-h5']: -0.01,
      ['heading-h6']: -0.01,
    },
    large: {
      ['heading-h1']: -0.01,
      ['heading-h2']: -0.01,
      ['heading-h3']: -0.01,
      ['heading-h4']: -0.01,
      ['heading-h5']: -0.01,
      ['heading-h6']: -0.01,
    },
  },
  spacious: {
    small: {
      ['heading-h1']: 0.01,
      ['heading-h2']: 0.01,
      ['heading-h3']: 0.01,
      ['heading-h4']: 0.01,
      ['heading-h5']: 0.01,
      ['heading-h6']: 0.01,
    },
    regular: {
      ['heading-h1']: 0.01,
      ['heading-h2']: 0.01,
      ['heading-h3']: 0.01,
      ['heading-h4']: 0.01,
      ['heading-h5']: 0.01,
      ['heading-h6']: 0.01,
    },
    large: {
      ['heading-h1']: 0.01,
      ['heading-h2']: 0.01,
      ['heading-h3']: 0.01,
      ['heading-h4']: 0.01,
      ['heading-h5']: 0.01,
      ['heading-h6']: 0.01,
    },
  },
  uppercase: {
    small: {
      ['heading-h1']: -0.01,
      ['heading-h2']: -0.01,
      ['heading-h3']: -0.01,
      ['heading-h4']: -0.01,
      ['heading-h5']: -0.01,
      ['heading-h6']: -0.01,
    },
    regular: {
      ['heading-h1']: -0.01,
      ['heading-h2']: -0.01,
      ['heading-h3']: -0.01,
      ['heading-h4']: -0.01,
      ['heading-h5']: -0.01,
      ['heading-h6']: -0.01,
    },
    large: {
      ['heading-h1']: -0.01,
      ['heading-h2']: -0.01,
      ['heading-h3']: -0.01,
      ['heading-h4']: -0.01,
      ['heading-h5']: -0.01,
      ['heading-h6']: -0.01,
    },
  },
};

type LineHeightKey =
  | 'heading-h1'
  | 'heading-h2'
  | 'heading-h3'
  | 'heading-h4'
  | 'heading-h5'
  | 'heading-h6'
  | 'body';

export const lineHeightValues: Record<
  TypeSettingStyle,
  Record<TypeSettingSize, Record<LineHeightKey, number>>
> = {
  default: {
    small: {
      ['heading-h1']: 1.2,
      ['heading-h2']: 1.2,
      ['heading-h3']: 1.2,
      ['heading-h4']: 1.3,
      ['heading-h5']: 1.4,
      ['heading-h6']: 1.4,
      ['body']: 1.5,
    },
    regular: {
      ['heading-h1']: 1.2,
      ['heading-h2']: 1.2,
      ['heading-h3']: 1.2,
      ['heading-h4']: 1.3,
      ['heading-h5']: 1.4,
      ['heading-h6']: 1.4,
      ['body']: 1.5,
    },
    large: {
      ['heading-h1']: 1.1,
      ['heading-h2']: 1.2,
      ['heading-h3']: 1.2,
      ['heading-h4']: 1.3,
      ['heading-h5']: 1.3,
      ['heading-h6']: 1.4,
      ['body']: 1.5,
    },
  },
  spacious: {
    small: {
      ['heading-h1']: 1.2,
      ['heading-h2']: 1.2,
      ['heading-h3']: 1.3,
      ['heading-h4']: 1.4,
      ['heading-h5']: 1.4,
      ['heading-h6']: 1.4,
      ['body']: 1.6,
    },
    regular: {
      ['heading-h1']: 1.2,
      ['heading-h2']: 1.2,
      ['heading-h3']: 1.2,
      ['heading-h4']: 1.4,
      ['heading-h5']: 1.4,
      ['heading-h6']: 1.4,
      ['body']: 1.6,
    },
    large: {
      ['heading-h1']: 1.1,
      ['heading-h2']: 1.2,
      ['heading-h3']: 1.2,
      ['heading-h4']: 1.2,
      ['heading-h5']: 1.2,
      ['heading-h6']: 1.2,
      ['body']: 1.6,
    },
  },
  uppercase: {
    small: {
      ['heading-h1']: 1,
      ['heading-h2']: 1,
      ['heading-h3']: 1,
      ['heading-h4']: 1.1,
      ['heading-h5']: 1.1,
      ['heading-h6']: 1.1,
      ['body']: 1.5,
    },
    regular: {
      ['heading-h1']: 1,
      ['heading-h2']: 1,
      ['heading-h3']: 1,
      ['heading-h4']: 1,
      ['heading-h5']: 1.1,
      ['heading-h6']: 1.1,
      ['body']: 1.5,
    },
    large: {
      ['heading-h1']: 1,
      ['heading-h2']: 1,
      ['heading-h3']: 1,
      ['heading-h4']: 1,
      ['heading-h5']: 1.1,
      ['heading-h6']: 1.1,
      ['body']: 1.5,
    },
  },
};

type StyleCssVariableKey =
  | 'heading-h1-letter-spacing'
  | 'heading-h2-letter-spacing'
  | 'heading-h3-letter-spacing'
  | 'heading-h4-letter-spacing'
  | 'heading-h5-letter-spacing'
  | 'heading-h6-letter-spacing'
  | 'heading-h1-line-height'
  | 'heading-h2-line-height'
  | 'heading-h3-line-height'
  | 'heading-h4-line-height'
  | 'heading-h5-line-height'
  | 'heading-h6-line-height'
  | 'body-line-height'
  | 'heading-text-transform';

export const styleCSSVariables: Record<
  TypeSettingStyle,
  Record<TypeSettingSize, Partial<Record<StyleCssVariableKey, string>>>
> = {
  default: {
    small: {
      ['heading-h1-letter-spacing']: `${
        letterSpacingValues.default.small['heading-h1'] *
        sizePixels.small['heading-h1-font-size']
      }px`,
      ['heading-h2-letter-spacing']: `${
        letterSpacingValues.default.small['heading-h2'] *
        sizePixels.small['heading-h2-font-size']
      }px`,
      ['heading-h3-letter-spacing']: `${
        letterSpacingValues.default.small['heading-h3'] *
        sizePixels.small['heading-h3-font-size']
      }px`,
      ['heading-h4-letter-spacing']: `${
        letterSpacingValues.default.small['heading-h4'] *
        sizePixels.small['heading-h4-font-size']
      }px`,
      ['heading-h5-letter-spacing']: `${
        letterSpacingValues.default.small['heading-h5'] *
        sizePixels.small['heading-h5-font-size']
      }px`,
      ['heading-h6-letter-spacing']: `${
        letterSpacingValues.default.small['heading-h6'] *
        sizePixels.small['heading-h6-font-size']
      }px`,

      ['heading-h1-line-height']: `${lineHeightValues.default.small['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.default.small['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.default.small['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.default.small['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.default.small['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.default.small['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.default.small['body']}`,
    },
    regular: {
      ['heading-h1-letter-spacing']: `${
        letterSpacingValues.default.regular['heading-h1'] *
        sizePixels.regular['heading-h1-font-size']
      }px`,
      ['heading-h2-letter-spacing']: `${
        letterSpacingValues.default.regular['heading-h2'] *
        sizePixels.regular['heading-h2-font-size']
      }px`,
      ['heading-h3-letter-spacing']: `${
        letterSpacingValues.default.regular['heading-h3'] *
        sizePixels.regular['heading-h3-font-size']
      }px`,
      ['heading-h4-letter-spacing']: `${
        letterSpacingValues.default.regular['heading-h4'] *
        sizePixels.regular['heading-h4-font-size']
      }px`,
      ['heading-h5-letter-spacing']: `${
        letterSpacingValues.default.regular['heading-h5'] *
        sizePixels.regular['heading-h5-font-size']
      }px`,
      ['heading-h6-letter-spacing']: `${
        letterSpacingValues.default.regular['heading-h6'] *
        sizePixels.regular['heading-h6-font-size']
      }px`,

      ['heading-h1-line-height']: `${lineHeightValues.default.regular['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.default.regular['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.default.regular['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.default.regular['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.default.regular['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.default.regular['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.default.regular['body']}`,
    },
    large: {
      ['heading-h1-letter-spacing']: `${
        letterSpacingValues.default.large['heading-h1'] *
        sizePixels.large['heading-h1-font-size']
      }px`,
      ['heading-h2-letter-spacing']: `${
        letterSpacingValues.default.large['heading-h2'] *
        sizePixels.large['heading-h2-font-size']
      }px`,
      ['heading-h3-letter-spacing']: `${
        letterSpacingValues.default.large['heading-h3'] *
        sizePixels.large['heading-h3-font-size']
      }px`,
      ['heading-h4-letter-spacing']: `${
        letterSpacingValues.default.large['heading-h4'] *
        sizePixels.large['heading-h4-font-size']
      }px`,
      ['heading-h5-letter-spacing']: `${
        letterSpacingValues.default.large['heading-h5'] *
        sizePixels.large['heading-h5-font-size']
      }px`,
      ['heading-h6-letter-spacing']: `${
        letterSpacingValues.default.large['heading-h6'] *
        sizePixels.large['heading-h6-font-size']
      }px`,

      ['heading-h1-line-height']: `${lineHeightValues.default.large['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.default.large['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.default.large['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.default.large['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.default.large['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.default.large['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.default.large['body']}`,
    },
  },
  spacious: {
    small: {
      ['heading-h1-letter-spacing']: `${
        letterSpacingValues.spacious.small['heading-h1'] *
        sizePixels.small['heading-h1-font-size']
      }px`,
      ['heading-h2-letter-spacing']: `${
        letterSpacingValues.spacious.small['heading-h2'] *
        sizePixels.small['heading-h2-font-size']
      }px`,
      ['heading-h3-letter-spacing']: `${
        letterSpacingValues.spacious.small['heading-h3'] *
        sizePixels.small['heading-h3-font-size']
      }px`,
      ['heading-h4-letter-spacing']: `${
        letterSpacingValues.spacious.small['heading-h4'] *
        sizePixels.small['heading-h4-font-size']
      }px`,
      ['heading-h5-letter-spacing']: `${
        letterSpacingValues.spacious.small['heading-h5'] *
        sizePixels.small['heading-h5-font-size']
      }px`,
      ['heading-h6-letter-spacing']: `${
        letterSpacingValues.spacious.small['heading-h6'] *
        sizePixels.small['heading-h6-font-size']
      }px`,

      ['heading-h1-line-height']: `${lineHeightValues.spacious.small['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.spacious.small['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.spacious.small['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.spacious.small['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.spacious.small['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.spacious.small['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.spacious.small['body']}`,
    },
    regular: {
      ['heading-h1-letter-spacing']: `${
        letterSpacingValues.spacious.regular['heading-h1'] *
        sizePixels.regular['heading-h1-font-size']
      }px`,
      ['heading-h2-letter-spacing']: `${
        letterSpacingValues.spacious.regular['heading-h2'] *
        sizePixels.regular['heading-h2-font-size']
      }px`,
      ['heading-h3-letter-spacing']: `${
        letterSpacingValues.spacious.regular['heading-h3'] *
        sizePixels.regular['heading-h3-font-size']
      }px`,
      ['heading-h4-letter-spacing']: `${
        letterSpacingValues.spacious.regular['heading-h4'] *
        sizePixels.regular['heading-h4-font-size']
      }px`,
      ['heading-h5-letter-spacing']: `${
        letterSpacingValues.spacious.regular['heading-h5'] *
        sizePixels.regular['heading-h5-font-size']
      }px`,
      ['heading-h6-letter-spacing']: `${
        letterSpacingValues.spacious.regular['heading-h6'] *
        sizePixels.regular['heading-h6-font-size']
      }px`,

      ['heading-h1-line-height']: `${lineHeightValues.spacious.regular['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.spacious.regular['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.spacious.regular['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.spacious.regular['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.spacious.regular['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.spacious.regular['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.spacious.regular['body']}`,
    },
    large: {
      ['heading-h1-letter-spacing']: `${
        letterSpacingValues.spacious.large['heading-h1'] *
        sizePixels.large['heading-h1-font-size']
      }px`,
      ['heading-h2-letter-spacing']: `${
        letterSpacingValues.spacious.large['heading-h2'] *
        sizePixels.large['heading-h2-font-size']
      }px`,
      ['heading-h3-letter-spacing']: `${
        letterSpacingValues.spacious.large['heading-h3'] *
        sizePixels.large['heading-h3-font-size']
      }px`,
      ['heading-h4-letter-spacing']: `${
        letterSpacingValues.spacious.large['heading-h4'] *
        sizePixels.large['heading-h4-font-size']
      }px`,
      ['heading-h5-letter-spacing']: `${
        letterSpacingValues.spacious.large['heading-h5'] *
        sizePixels.large['heading-h5-font-size']
      }px`,
      ['heading-h6-letter-spacing']: `${
        letterSpacingValues.spacious.large['heading-h6'] *
        sizePixels.large['heading-h6-font-size']
      }px`,

      ['heading-h1-line-height']: `${lineHeightValues.spacious.large['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.spacious.large['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.spacious.large['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.spacious.large['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.spacious.large['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.spacious.large['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.spacious.large['body']}`,
    },
  },
  uppercase: {
    small: {
      ['heading-h1-letter-spacing']: `${
        letterSpacingValues.uppercase.small['heading-h1'] *
        sizePixels.small['heading-h1-font-size']
      }px`,
      ['heading-h2-letter-spacing']: `${
        letterSpacingValues.uppercase.small['heading-h2'] *
        sizePixels.small['heading-h2-font-size']
      }px`,
      ['heading-h3-letter-spacing']: `${
        letterSpacingValues.uppercase.small['heading-h3'] *
        sizePixels.small['heading-h3-font-size']
      }px`,
      ['heading-h4-letter-spacing']: `${
        letterSpacingValues.uppercase.small['heading-h4'] *
        sizePixels.small['heading-h4-font-size']
      }px`,
      ['heading-h5-letter-spacing']: `${
        letterSpacingValues.uppercase.small['heading-h5'] *
        sizePixels.small['heading-h5-font-size']
      }px`,
      ['heading-h6-letter-spacing']: `${
        letterSpacingValues.uppercase.small['heading-h6'] *
        sizePixels.small['heading-h6-font-size']
      }px`,

      ['heading-h1-line-height']: `${lineHeightValues.uppercase.small['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.uppercase.small['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.uppercase.small['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.uppercase.small['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.uppercase.small['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.uppercase.small['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.uppercase.small['body']}`,

      ['heading-text-transform']: 'uppercase',
    },
    regular: {
      ['heading-h1-letter-spacing']: `${
        letterSpacingValues.uppercase.regular['heading-h1'] *
        sizePixels.regular['heading-h1-font-size']
      }px`,
      ['heading-h2-letter-spacing']: `${
        letterSpacingValues.uppercase.regular['heading-h2'] *
        sizePixels.regular['heading-h2-font-size']
      }px`,
      ['heading-h3-letter-spacing']: `${
        letterSpacingValues.uppercase.regular['heading-h3'] *
        sizePixels.regular['heading-h3-font-size']
      }px`,
      ['heading-h4-letter-spacing']: `${
        letterSpacingValues.uppercase.regular['heading-h4'] *
        sizePixels.regular['heading-h4-font-size']
      }px`,
      ['heading-h5-letter-spacing']: `${
        letterSpacingValues.uppercase.regular['heading-h5'] *
        sizePixels.regular['heading-h5-font-size']
      }px`,
      ['heading-h6-letter-spacing']: `${
        letterSpacingValues.uppercase.regular['heading-h6'] *
        sizePixels.regular['heading-h6-font-size']
      }px`,

      ['heading-h1-line-height']: `${lineHeightValues.uppercase.regular['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.uppercase.regular['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.uppercase.regular['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.uppercase.regular['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.uppercase.regular['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.uppercase.regular['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.uppercase.regular['body']}`,

      ['heading-text-transform']: 'uppercase',
    },
    large: {
      ['heading-h1-letter-spacing']: `${
        letterSpacingValues.uppercase.large['heading-h1'] *
        sizePixels.large['heading-h1-font-size']
      }px`,
      ['heading-h2-letter-spacing']: `${
        letterSpacingValues.uppercase.large['heading-h2'] *
        sizePixels.large['heading-h2-font-size']
      }px`,
      ['heading-h3-letter-spacing']: `${
        letterSpacingValues.uppercase.large['heading-h3'] *
        sizePixels.large['heading-h3-font-size']
      }px`,
      ['heading-h4-letter-spacing']: `${
        letterSpacingValues.uppercase.large['heading-h4'] *
        sizePixels.large['heading-h4-font-size']
      }px`,
      ['heading-h5-letter-spacing']: `${
        letterSpacingValues.uppercase.large['heading-h5'] *
        sizePixels.large['heading-h5-font-size']
      }px`,
      ['heading-h6-letter-spacing']: `${
        letterSpacingValues.uppercase.large['heading-h6'] *
        sizePixels.large['heading-h6-font-size']
      }px`,

      ['heading-h1-line-height']: `${lineHeightValues.uppercase.large['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.uppercase.large['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.uppercase.large['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.uppercase.large['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.uppercase.large['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.uppercase.large['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.uppercase.large['body']}`,

      ['heading-text-transform']: 'uppercase',
    },
  },
};

export const styleCSSVariablesRem: Record<
  TypeSettingStyle,
  Record<TypeSettingSize, Partial<Record<StyleCssVariableKey, string>>>
> = {
  default: {
    small: {
      ['heading-h1-letter-spacing']: `${
        (letterSpacingValues.default.small['heading-h1'] *
          sizePixels.small['heading-h1-font-size']) /
        16
      }rem`,
      ['heading-h2-letter-spacing']: `${
        (letterSpacingValues.default.small['heading-h2'] *
          sizePixels.small['heading-h2-font-size']) /
        16
      }rem`,
      ['heading-h3-letter-spacing']: `${
        (letterSpacingValues.default.small['heading-h3'] *
          sizePixels.small['heading-h3-font-size']) /
        16
      }rem`,
      ['heading-h4-letter-spacing']: `${
        (letterSpacingValues.default.small['heading-h4'] *
          sizePixels.small['heading-h4-font-size']) /
        16
      }rem`,
      ['heading-h5-letter-spacing']: `${
        (letterSpacingValues.default.small['heading-h5'] *
          sizePixels.small['heading-h5-font-size']) /
        16
      }rem`,
      ['heading-h6-letter-spacing']: `${
        (letterSpacingValues.default.small['heading-h6'] *
          sizePixels.small['heading-h6-font-size']) /
        16
      }rem`,

      ['heading-h1-line-height']: `${lineHeightValues.default.small['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.default.small['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.default.small['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.default.small['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.default.small['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.default.small['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.default.small['body']}`,
    },
    regular: {
      ['heading-h1-letter-spacing']: `${
        (letterSpacingValues.default.regular['heading-h1'] *
          sizePixels.regular['heading-h1-font-size']) /
        16
      }rem`,
      ['heading-h2-letter-spacing']: `${
        (letterSpacingValues.default.regular['heading-h2'] *
          sizePixels.regular['heading-h2-font-size']) /
        16
      }rem`,
      ['heading-h3-letter-spacing']: `${
        (letterSpacingValues.default.regular['heading-h3'] *
          sizePixels.regular['heading-h3-font-size']) /
        16
      }rem`,
      ['heading-h4-letter-spacing']: `${
        (letterSpacingValues.default.regular['heading-h4'] *
          sizePixels.regular['heading-h4-font-size']) /
        16
      }rem`,
      ['heading-h5-letter-spacing']: `${
        (letterSpacingValues.default.regular['heading-h5'] *
          sizePixels.regular['heading-h5-font-size']) /
        16
      }rem`,
      ['heading-h6-letter-spacing']: `${
        (letterSpacingValues.default.regular['heading-h6'] *
          sizePixels.regular['heading-h6-font-size']) /
        16
      }rem`,

      ['heading-h1-line-height']: `${lineHeightValues.default.regular['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.default.regular['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.default.regular['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.default.regular['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.default.regular['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.default.regular['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.default.regular['body']}`,
    },
    large: {
      ['heading-h1-letter-spacing']: `${
        (letterSpacingValues.default.large['heading-h1'] *
          sizePixels.large['heading-h1-font-size']) /
        16
      }rem`,
      ['heading-h2-letter-spacing']: `${
        (letterSpacingValues.default.large['heading-h2'] *
          sizePixels.large['heading-h2-font-size']) /
        16
      }rem`,
      ['heading-h3-letter-spacing']: `${
        (letterSpacingValues.default.large['heading-h3'] *
          sizePixels.large['heading-h3-font-size']) /
        16
      }rem`,
      ['heading-h4-letter-spacing']: `${
        (letterSpacingValues.default.large['heading-h4'] *
          sizePixels.large['heading-h4-font-size']) /
        16
      }rem`,
      ['heading-h5-letter-spacing']: `${
        (letterSpacingValues.default.large['heading-h5'] *
          sizePixels.large['heading-h5-font-size']) /
        16
      }rem`,
      ['heading-h6-letter-spacing']: `${
        (letterSpacingValues.default.large['heading-h6'] *
          sizePixels.large['heading-h6-font-size']) /
        16
      }rem`,

      ['heading-h1-line-height']: `${lineHeightValues.default.large['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.default.large['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.default.large['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.default.large['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.default.large['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.default.large['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.default.large['body']}rem`,
    },
  },
  spacious: {
    small: {
      ['heading-h1-letter-spacing']: `${
        (letterSpacingValues.spacious.small['heading-h1'] *
          sizePixels.small['heading-h1-font-size']) /
        16
      }rem`,
      ['heading-h2-letter-spacing']: `${
        (letterSpacingValues.spacious.small['heading-h2'] *
          sizePixels.small['heading-h2-font-size']) /
        16
      }rem`,
      ['heading-h3-letter-spacing']: `${
        (letterSpacingValues.spacious.small['heading-h3'] *
          sizePixels.small['heading-h3-font-size']) /
        16
      }rem`,
      ['heading-h4-letter-spacing']: `${
        (letterSpacingValues.spacious.small['heading-h4'] *
          sizePixels.small['heading-h4-font-size']) /
        16
      }rem`,
      ['heading-h5-letter-spacing']: `${
        (letterSpacingValues.spacious.small['heading-h5'] *
          sizePixels.small['heading-h5-font-size']) /
        16
      }rem`,
      ['heading-h6-letter-spacing']: `${
        (letterSpacingValues.spacious.small['heading-h6'] *
          sizePixels.small['heading-h6-font-size']) /
        16
      }rem`,

      ['heading-h1-line-height']: `${lineHeightValues.spacious.small['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.spacious.small['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.spacious.small['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.spacious.small['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.spacious.small['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.spacious.small['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.spacious.small['body']}`,
    },
    regular: {
      ['heading-h1-letter-spacing']: `${
        (letterSpacingValues.spacious.regular['heading-h1'] *
          sizePixels.regular['heading-h1-font-size']) /
        16
      }rem`,
      ['heading-h2-letter-spacing']: `${
        (letterSpacingValues.spacious.regular['heading-h2'] *
          sizePixels.regular['heading-h2-font-size']) /
        16
      }rem`,
      ['heading-h3-letter-spacing']: `${
        (letterSpacingValues.spacious.regular['heading-h3'] *
          sizePixels.regular['heading-h3-font-size']) /
        16
      }rem`,
      ['heading-h4-letter-spacing']: `${
        (letterSpacingValues.spacious.regular['heading-h4'] *
          sizePixels.regular['heading-h4-font-size']) /
        16
      }rem`,
      ['heading-h5-letter-spacing']: `${
        (letterSpacingValues.spacious.regular['heading-h5'] *
          sizePixels.regular['heading-h5-font-size']) /
        16
      }rem`,
      ['heading-h6-letter-spacing']: `${
        (letterSpacingValues.spacious.regular['heading-h6'] *
          sizePixels.regular['heading-h6-font-size']) /
        16
      }rem`,

      ['heading-h1-line-height']: `${lineHeightValues.spacious.regular['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.spacious.regular['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.spacious.regular['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.spacious.regular['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.spacious.regular['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.spacious.regular['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.spacious.regular['body']}`,
    },
    large: {
      ['heading-h1-letter-spacing']: `${
        (letterSpacingValues.spacious.large['heading-h1'] *
          sizePixels.large['heading-h1-font-size']) /
        16
      }rem`,
      ['heading-h2-letter-spacing']: `${
        (letterSpacingValues.spacious.large['heading-h2'] *
          sizePixels.large['heading-h2-font-size']) /
        16
      }rem`,
      ['heading-h3-letter-spacing']: `${
        (letterSpacingValues.spacious.large['heading-h3'] *
          sizePixels.large['heading-h3-font-size']) /
        16
      }rem`,
      ['heading-h4-letter-spacing']: `${
        (letterSpacingValues.spacious.large['heading-h4'] *
          sizePixels.large['heading-h4-font-size']) /
        16
      }rem`,
      ['heading-h5-letter-spacing']: `${
        (letterSpacingValues.spacious.large['heading-h5'] *
          sizePixels.large['heading-h5-font-size']) /
        16
      }rem`,
      ['heading-h6-letter-spacing']: `${
        (letterSpacingValues.spacious.large['heading-h6'] *
          sizePixels.large['heading-h6-font-size']) /
        16
      }rem`,

      ['heading-h1-line-height']: `${lineHeightValues.spacious.large['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.spacious.large['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.spacious.large['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.spacious.large['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.spacious.large['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.spacious.large['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.spacious.large['body']}`,
    },
  },
  uppercase: {
    small: {
      ['heading-h1-letter-spacing']: `${
        (letterSpacingValues.uppercase.small['heading-h1'] *
          sizePixels.small['heading-h1-font-size']) /
        16
      }rem`,
      ['heading-h2-letter-spacing']: `${
        (letterSpacingValues.uppercase.small['heading-h2'] *
          sizePixels.small['heading-h2-font-size']) /
        16
      }rem`,
      ['heading-h3-letter-spacing']: `${
        (letterSpacingValues.uppercase.small['heading-h3'] *
          sizePixels.small['heading-h3-font-size']) /
        16
      }rem`,
      ['heading-h4-letter-spacing']: `${
        (letterSpacingValues.uppercase.small['heading-h4'] *
          sizePixels.small['heading-h4-font-size']) /
        16
      }rem`,
      ['heading-h5-letter-spacing']: `${
        (letterSpacingValues.uppercase.small['heading-h5'] *
          sizePixels.small['heading-h5-font-size']) /
        16
      }rem`,
      ['heading-h6-letter-spacing']: `${
        (letterSpacingValues.uppercase.small['heading-h6'] *
          sizePixels.small['heading-h6-font-size']) /
        16
      }rem`,

      ['heading-h1-line-height']: `${lineHeightValues.uppercase.small['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.uppercase.small['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.uppercase.small['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.uppercase.small['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.uppercase.small['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.uppercase.small['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.uppercase.small['body']}`,

      ['heading-text-transform']: 'uppercase',
    },
    regular: {
      ['heading-h1-letter-spacing']: `${
        (letterSpacingValues.uppercase.regular['heading-h1'] *
          sizePixels.regular['heading-h1-font-size']) /
        16
      }rem`,
      ['heading-h2-letter-spacing']: `${
        (letterSpacingValues.uppercase.regular['heading-h2'] *
          sizePixels.regular['heading-h2-font-size']) /
        16
      }rem`,
      ['heading-h3-letter-spacing']: `${
        (letterSpacingValues.uppercase.regular['heading-h3'] *
          sizePixels.regular['heading-h3-font-size']) /
        16
      }rem`,
      ['heading-h4-letter-spacing']: `${
        (letterSpacingValues.uppercase.regular['heading-h4'] *
          sizePixels.regular['heading-h4-font-size']) /
        16
      }rem`,
      ['heading-h5-letter-spacing']: `${
        (letterSpacingValues.uppercase.regular['heading-h5'] *
          sizePixels.regular['heading-h5-font-size']) /
        16
      }rem`,
      ['heading-h6-letter-spacing']: `${
        (letterSpacingValues.uppercase.regular['heading-h6'] *
          sizePixels.regular['heading-h6-font-size']) /
        16
      }rem`,

      ['heading-h1-line-height']: `${lineHeightValues.uppercase.regular['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.uppercase.regular['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.uppercase.regular['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.uppercase.regular['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.uppercase.regular['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.uppercase.regular['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.uppercase.regular['body']}`,

      ['heading-text-transform']: 'uppercase',
    },
    large: {
      ['heading-h1-letter-spacing']: `${
        (letterSpacingValues.uppercase.large['heading-h1'] *
          sizePixels.large['heading-h1-font-size']) /
        16
      }rem`,
      ['heading-h2-letter-spacing']: `${
        (letterSpacingValues.uppercase.large['heading-h2'] *
          sizePixels.large['heading-h2-font-size']) /
        16
      }rem`,
      ['heading-h3-letter-spacing']: `${
        (letterSpacingValues.uppercase.large['heading-h3'] *
          sizePixels.large['heading-h3-font-size']) /
        16
      }rem`,
      ['heading-h4-letter-spacing']: `${
        (letterSpacingValues.uppercase.large['heading-h4'] *
          sizePixels.large['heading-h4-font-size']) /
        16
      }rem`,
      ['heading-h5-letter-spacing']: `${
        (letterSpacingValues.uppercase.large['heading-h5'] *
          sizePixels.large['heading-h5-font-size']) /
        16
      }rem`,
      ['heading-h6-letter-spacing']: `${
        (letterSpacingValues.uppercase.large['heading-h6'] *
          sizePixels.large['heading-h6-font-size']) /
        16
      }rem`,

      ['heading-h1-line-height']: `${lineHeightValues.uppercase.large['heading-h1']}`,
      ['heading-h2-line-height']: `${lineHeightValues.uppercase.large['heading-h2']}`,
      ['heading-h3-line-height']: `${lineHeightValues.uppercase.large['heading-h3']}`,
      ['heading-h4-line-height']: `${lineHeightValues.uppercase.large['heading-h4']}`,
      ['heading-h5-line-height']: `${lineHeightValues.uppercase.large['heading-h5']}`,
      ['heading-h6-line-height']: `${lineHeightValues.uppercase.large['heading-h6']}`,
      ['body-line-height']: `${lineHeightValues.uppercase.large['body']}`,

      ['heading-text-transform']: 'uppercase',
    },
  },
};

export const generateTypeSettingsCSSVariables = (
  settings: TypeSettingsState,
): Record<string, string> => {
  return {
    ...Object.fromEntries(
      Object.entries(sizeCSSVariables[settings.size]).map(([k, v]) => {
        if (settings.style === 'uppercase' && k.startsWith('heading-')) {
          return [k, `round(up, calc(${v} * 0.8), 1px)`];
        }
        return [k, v];
      }),
    ),
    ...weightCSSVariables[settings.weight],
    ...styleCSSVariables[settings.style][settings.size],
  };
};

class TypeSettingsOverrideStore {
  @observable.deep
  typeSettingsOverrides: Partial<TypeSettingsState> = {};

  constructor() {
    makeObservable(this);
  }
}

export const globalTypeSettingsOverrideStore = new TypeSettingsOverrideStore();

export const HEADING_FONT_WEIGHTS: Record<TypeSettingWeight, number> = {
  regular: 400,
  medium: 500,
  bold: 700,
};
